/**
 * Created by andreyna on 6/1/2016.
 */
import GNWPServiceModel from './gnwp_model';
import BaseService from '../../base/base_service';
import _ from '../../utils/underscore';
import Device from '../../utils/device/device';
import GNWPResponse from './gnwp_response';
import { IGNWPRequestModel } from './gnwp_service_interface';
import Store from "../../utils/store";
import {REGISTRATION_DATA_KEY} from "../../components/external_links/handlers/membership/external_links_membership_handler_consts";
import {STATE_OF_RESIDENCE_COOKIE_KEY} from "../../../../common/const/state-of-residence-cookie_const";


const VERSION_MAX_SIZE = 50;

export enum DEVICE_ENUM_TYPE {
    NON = 0,
    IOS = 1,
    ANDROID = 2
};

/**
 * GnwpService enables perform navigation / redirect appropriate to specified template and some additional parameters.
 * Template describes the service (web site) GNWP should redirect to.
 */
class GnwpService extends BaseService {
    /**
     * @static
     * @type {string}
     */
    static CLASS = 'GnwpService';


    /**
     * @property
     * @type {null}
     * @private
     */
    _model: GNWPServiceModel = null;


    /**
     *
     * @param params
     */
    init(params) {
        super.init(params);
        this._model = GNWPServiceModel.create(params);
    }


    /**
     * Get GNWPResponse object contains API to perform GNWP navigation (see GNWPResponse class)
     *
     * @usage:
     * getGNWPResponse({"templateName" : "netent", "gameType" : 2010005, "isReal" : true})
     *
     * @param {Object} GNWPRequestModel - {
     * templateName (string)- template describe service (web site) GNWP redirects to. REQUIRED
     * gameType (number) - game identifier to open remote game. REQUIRED in game templates
     * isReal (bool) - if it's real game or fun. NOT REQUIRED
     * }
     * There are some templates with additional parameters.
     *
     * Additional parameters in netent template
     * gameLimit (number) NOT REQUIRED
     * requestedTimeLimit (number) NOT REQUIRED
     * restrictionPeriod (number) NOT REQUIRED
     *
     * Additional parameters in omg template
     * viewPort  REQUIRED
     *
     * Used for request marketing data from B2X
     *
     * @return Promise
     *
     */
    getGNWPResponse(GNWPRequestModel: IGNWPRequestModel): Promise<GNWPResponse> {
        return this._getMarketingData(GNWPRequestModel).then((marketingRes) => {
            const GNWPRequest = Object.assign({}, marketingRes, GNWPRequestModel);
            return this._getGNWPParams(GNWPRequest)
        }).then((res: any) => {
            if (res.error_code) {
                throw new Error(res);
            }
            const response = GNWPResponse.create({
                baseUrl: this._model.getRedirectUri(),
                navigation: this._replaceNavigationParams(res)
            });
            return response;
        });
    }

    _getMarketingData(GNWPRequestModel: IGNWPRequestModel) {

        const marketingData = {};
        const integrationService = this.serviceFactory.integrationService();
        if (integrationService.isMarketingDataExist()) {
            const tempMarketData = integrationService.getRecentMarketData();

            // Use it when there are complex objects in marketData
            Object.keys(tempMarketData).forEach((key) => {
                if (_.isString(tempMarketData[key])) {
                    marketingData[key] = tempMarketData[key];
                } else {
                    marketingData[key] = JSON.stringify(tempMarketData[key]);
                }
            });
        }

        return this.serviceFactory.skinService().getSerialData(marketingData, GNWPRequestModel).then(data => {
            if (Device.hybrid.inCasino) {
                this._appendMarketingDataToTestData(data);
            }
            return this.serviceFactory.hybridService().getLexisNexisSession(data);
        });
    }

    private _appendMarketingDataToTestData(data: {[key: string]: any}) {
        try {
            if (!data) {
                throw new Error("Invalid object received");
            }
            let testData: any = this.serviceFactory.hybridService().getAppMareketingData();
            if (data.TestData) {
                testData = {
                  ...JSON.parse(data.TestData),
                  ...testData
               }
            }
            data.TestData = JSON.stringify(testData);
        } catch (err) {
            this.logger.warn("Failed to add marketingData to data.TestData", err);
        }
    }

    /**
     * Check if template is valid string for GNWP navigation
     *
     * @param {string} template
     * @returns {boolean} false = GNWP template is not corrected, true = otherwise
     */
    isGNWPTemplate(template) {
        if (!template) return false;
        return this._model.isValidTemplate(template);
    }


    /**
     * Validate correct definition of GNWP request object
     *
     * @param {Object} request - GNWP request object. See runGNWPRedirect function description
     * @returns {boolean} false = GNWP request object doesn't contain [templateName] or [templateName] is not corrected, true = otherwise
     * @private
     */
    _isValidRequest(request) {
        if (!request) return false;

        if (!request.templateName) {
            this.logger.warn('templateName property is not found in gnwpRequestModel');
            return false;
        }

        return true;
    }


    /**
     * TODO We need to provide another solution to version size limitation problem
     *
     * There is a limitation of version max length in GNWP.
     * Function cuts version string to it's max size
     *
     * @param {string} version
     * @returns {string}
     * @private
     */
    _fixVersionMaxSize(version) {
        let fixVersion = version;
        if (!_.isUndefined(version) && _.isString(version)) {
            const sizeDiff = version.length - VERSION_MAX_SIZE;
            const startIndex = sizeDiff < 0 ? 0 : sizeDiff;
            fixVersion = version.slice(startIndex);
        }

        return fixVersion;
    }


    private getDeviceTypeForGNWP(): string {
        if (Device.hybrid.isEmbedded) {
            return 'hybrid_ios_embedded';
        }
        return "";
    }

    private getNativeType(): DEVICE_ENUM_TYPE {
        if (Device.hybrid.inCasino && Device.os.isIOS) {
            return DEVICE_ENUM_TYPE.IOS;
        } else if (Device.hybrid.inCasino && Device.os.isAndroid) {
            return DEVICE_ENUM_TYPE.ANDROID;
        }

        return DEVICE_ENUM_TYPE.NON;
    }

    _getUserSelectedState(): string {
        return Store.getCookie(STATE_OF_RESIDENCE_COOKIE_KEY);
    }

    /**
     * Performs request to server (API) and retrieves navigation parameters, appropriate to GNWP object properties
     *
     * @param {Object} request - GNWP request object. See runGNWPRedirect function description
     * @returns {Promise}
     * @private
     */
    _getGNWPParams(GNWPRequestModel: IGNWPRequestModel): Promise<any> {
        const GNWPUri = this._model.getGNWPUri();
        const promise = new Promise((resolve, reject) => {
            if (!this._isValidRequest(GNWPRequestModel)) {
                reject(null);
            } else {
                const skinService = this.serviceFactory.skinService();
                GNWPRequestModel.brandID = skinService.getBrandID();
                GNWPRequestModel.subBrandID = skinService.getSubBrandID();
                GNWPRequestModel.productPackage = skinService.getProductPackageID();
                GNWPRequestModel.isMobile = Device.os.isMobileOS;
                GNWPRequestModel.version = this._fixVersionMaxSize(skinService.getVersion());
                GNWPRequestModel.isNative = 0;
                GNWPRequestModel.storeRegistrationCookiesData = Store.get(REGISTRATION_DATA_KEY, '{}');
                GNWPRequestModel.currentRoute = this.serviceFactory.windowService().getPathName();
                GNWPRequestModel.googleSessionID = this.serviceFactory.analyticsService().getGoogleSessionID();
                GNWPRequestModel.userSelectedState = this._getUserSelectedState();

                // GNWPRequestModel.isMobile = true;
                // GNWPRequestModel.hybridBundleParams = 'co.dino://ddadlaskddjasdjasd123143';
                // GNWPRequestModel.hybridNativeType = 1;// 0: non 1: ios 2: android

                if (Device.hybrid.inCasino) {
                    const defaultHybridSerial = skinService.getDefaultHybridSerial();
                    if(defaultHybridSerial){
                        GNWPRequestModel.sr = defaultHybridSerial;
                    }
                    GNWPRequestModel.hybridScheme = this.serviceFactory.hybridService().getUrlSchemeToOpen();
                    GNWPRequestModel.isNative = 1;
                }
                GNWPRequestModel.device = this.getDeviceTypeForGNWP();
                GNWPRequestModel.hybridNativeType = this.getNativeType();

                GNWPRequestModel.regId = this.serviceFactory.regulationService().getRegulationCode();
                if (this.serviceFactory.oAuth2Service().isAuthorized()) {
                    GNWPRequestModel.registrationCountry = this.serviceFactory.userService().getAccountData().RegistrationCountry;
                }

                this.serviceFactory.http2Service().post(GNWPUri, GNWPRequestModel)
                    .then((res) => {
                        if (res) {
                            resolve(res);
                        } else {
                            this.logger.error('GNWP request failed');
                            reject('GNWP response is null');
                        }
                    })
                    .catch(function (reason) { // (B)
                        // File read error or JSON SyntaxError
                        reject(reason);
                    });
            }
        });

        return promise;
    }

    _replaceNavigationParams(navResp) {
        for (const param in navResp) {
            if (_.isFunction(this['_replace' + param])) {
                navResp[param] = this['_replace' + param](navResp[param]);
            }
        }

        return navResp;
    }

    /**
     *
     * @param val
     * @returns {XML|void|string|*}
     * @private
     */
    _replaceSpecificNavigation(val) {
        const refreshToken = this.serviceFactory.oAuth2Service().extractTokenProperty('refresh_token');

        return val.replace('{{refresh_token}}', refreshToken);
    }
}

export default GnwpService;
